<template>
  <!--
    Phase 2 (2026-08-25, silent read failures).

    Mounted once at the app root, NOT per page. That is the point: 35 of the 37
    getCollection call sites read only `documents`, so any fix that depended on a
    consumer rendering an error state would have been a fix in name only. Here,
    the consumer is not the thing doing the rendering, and no call site had to be
    edited for the fix to hold.

    Two shapes for two situations, per the measured terminal/transient split in
    reportQueryErrors.js:

    TERMINAL — a blocking panel. The listener is dead (proven in
    onSnapshotErrorBehaviour.rules.test.js), the page below is empty or stale and
    cannot recover, and the person needs to be told why and given a way out.
    Blocking is correct here precisely because there is nothing usable behind it.

    TRANSIENT — a non-blocking strip. The last good data is still on screen and
    still useful; destroying a manager's roster mid-service over a blip would be
    its own outage. It says what is wrong and stays out of the way.
  -->
  <div v-if="terminal" class="dh-overlay" role="alertdialog" aria-modal="true" aria-labelledby="dh-title">
    <div class="dh-panel">
      <div class="dh-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 9v4" /><path d="M12 17h.01" />
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
      </div>
      <div id="dh-title" class="dh-title">{{ terminal.title }}</div>
      <div class="dh-message">{{ terminal.message }}</div>
      <button v-if="terminal.action" class="dh-btn" @click="signOut" :disabled="isPending">
        {{ isPending ? 'Signing out…' : terminal.action }}
      </button>
      <div class="dh-support">
        Need a hand? <a href="mailto:support@rosterem.com.au">support@rosterem.com.au</a>
      </div>
    </div>
  </div>

  <div v-else-if="hasTransient" class="dh-strip" role="status">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="dh-strip-icon">
      <path d="M12 9v4" /><path d="M12 17h.01" /><circle cx="12" cy="12" r="10" />
    </svg>
    <span>Couldn't refresh — what you're seeing may be out of date.</span>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import dataHealthStore from '@/stores/dataHealthStore';

export default {
  name: 'DataHealthBanner',
  setup() {
    const { logout } = useAuth0();
    const isPending = ref(false);

    const terminal = computed(() => dataHealthStore.state.terminal);
    const hasTransient = computed(() => dataHealthStore.state.transientSources.length > 0);

    // The same real Auth0 logout Navbar.vue, Profile.vue and NoOrganisation.vue
    // use — a full-page redirect that actually clears the session cookie, not a
    // client-side flag flip that leaves the next boot in the same broken state.
    const signOut = () => {
      isPending.value = true;
      dataHealthStore.resetDataHealth();
      logout({ logoutParams: { returnTo: window.location.origin } });
    };

    return { terminal, hasTransient, isPending, signOut };
  },
};
</script>

<style scoped>
.dh-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
}

.dh-panel {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 14px;
  padding: 28px 26px 22px;
  text-align: center;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.25);
}

.dh-icon {
  width: 46px;
  height: 46px;
  margin: 0 auto 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef3c7;
  color: #d97706;
}

.dh-icon svg { width: 24px; height: 24px; }

.dh-title {
  font-size: 19px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
}

.dh-message {
  font-size: 14px;
  line-height: 1.55;
  color: #475569;
  margin-bottom: 20px;
}

.dh-btn {
  width: 100%;
  border: none;
  border-radius: 9px;
  padding: 11px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: #1e293b;
  cursor: pointer;
}

.dh-btn:disabled { opacity: 0.6; cursor: default; }

.dh-support {
  margin-top: 14px;
  font-size: 12px;
  color: #64748b;
}

.dh-support a { color: #2563eb; }

.dh-strip {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #78350f;
  background: #fef3c7;
  border-bottom: 1px solid #fcd34d;
}

.dh-strip-icon { width: 15px; height: 15px; flex: none; }
</style>
